import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { createTicket, getCategories, uploadAttachment } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Loader2, 
    ArrowLeft, 
    Ticket, 
    AlertCircle, 
    CheckCircle,
    Upload,
    X,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';

export default function CreateTicketPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        oggetto: '',
        descrizione: '',
        categoria: '',
        priorita: 'Media'
    });
    const [files, setFiles] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [createdTicket, setCreatedTicket] = useState(null);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await getCategories();
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const validFiles = selectedFiles.filter(file => file.size <= 5 * 1024 * 1024);
        
        if (validFiles.length !== selectedFiles.length) {
            toast.error('Alcuni file superano il limite di 5MB');
        }
        
        setFiles([...files, ...validFiles]);
    };

    const removeFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await createTicket(formData);
            const ticketId = response.data.id;
            
            // Upload attachments if any
            for (const file of files) {
                try {
                    await uploadAttachment(ticketId, file);
                } catch (err) {
                    console.error('Error uploading file:', err);
                }
            }
            
            setCreatedTicket(response.data);
            setSuccess(true);
            toast.success('Ticket creato con successo!');
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore durante la creazione del ticket');
        } finally {
            setLoading(false);
        }
    };

    if (success && createdTicket) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md border-slate-200 animate-fade-in">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">Ticket Creato!</h2>
                            <p className="text-slate-600 mb-4">
                                Il tuo ticket è stato creato con successo.
                            </p>
                            <div className="bg-slate-100 rounded-lg p-4 mb-6">
                                <p className="text-sm text-slate-500 mb-1">Numero Ticket</p>
                                <code className="text-lg font-mono font-bold text-indigo-600">
                                    {createdTicket.numero_ticket}
                                </code>
                            </div>
                            <p className="text-sm text-slate-500 mb-6">
                                Riceverai una email di conferma all'indirizzo {user?.email}
                            </p>
                            <div className="flex gap-3">
                                <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    Torna alla Dashboard
                                </Button>
                                <Button 
                                    className="flex-1 bg-indigo-500 hover:bg-indigo-600"
                                    onClick={() => navigate(`/tickets/${createdTicket.id}`)}
                                >
                                    Vedi Ticket
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center h-16">
                        <Button 
                            variant="ghost" 
                            onClick={() => navigate('/dashboard')}
                            className="mr-4"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Indietro
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                                <Ticket className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-slate-900">Nuovo Ticket</h1>
                                <p className="text-xs text-slate-500">Compila il form per richiedere assistenza</p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Card className="border-slate-200 animate-fade-in">
                    <CardHeader>
                        <CardTitle>Dettagli del Ticket</CardTitle>
                        <CardDescription>
                            Descrivi il tuo problema in modo dettagliato per aiutarci a risolverlo più velocemente
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {error && (
                                <Alert variant="destructive" className="bg-red-50 border-red-200">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="oggetto">Oggetto *</Label>
                                <Input
                                    id="oggetto"
                                    name="oggetto"
                                    placeholder="Breve descrizione del problema"
                                    value={formData.oggetto}
                                    onChange={handleChange}
                                    required
                                    data-testid="ticket-oggetto-input"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="categoria">Categoria *</Label>
                                    <Select 
                                        value={formData.categoria} 
                                        onValueChange={(value) => setFormData({...formData, categoria: value})}
                                        required
                                    >
                                        <SelectTrigger data-testid="ticket-categoria-select">
                                            <SelectValue placeholder="Seleziona categoria" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories.map((cat) => (
                                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="priorita">Priorità *</Label>
                                    <Select 
                                        value={formData.priorita} 
                                        onValueChange={(value) => setFormData({...formData, priorita: value})}
                                    >
                                        <SelectTrigger data-testid="ticket-priorita-select">
                                            <SelectValue placeholder="Seleziona priorità" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Bassa">Bassa</SelectItem>
                                            <SelectItem value="Media">Media</SelectItem>
                                            <SelectItem value="Alta">Alta</SelectItem>
                                            <SelectItem value="Urgente">Urgente</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="descrizione">Descrizione *</Label>
                                <Textarea
                                    id="descrizione"
                                    name="descrizione"
                                    placeholder="Descrivi il problema in modo dettagliato. Includi eventuali messaggi di errore, passaggi per riprodurre il problema, etc."
                                    value={formData.descrizione}
                                    onChange={handleChange}
                                    rows={6}
                                    required
                                    data-testid="ticket-descrizione-input"
                                />
                            </div>

                            {/* File Upload */}
                            <div className="space-y-2">
                                <Label>Allegati (opzionale)</Label>
                                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-300 transition-colors">
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        className="hidden"
                                        id="file-upload"
                                        data-testid="ticket-file-input"
                                    />
                                    <label htmlFor="file-upload" className="cursor-pointer">
                                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                                        <p className="text-sm text-slate-600">
                                            Clicca per caricare o trascina i file qui
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">
                                            Max 5MB per file
                                        </p>
                                    </label>
                                </div>
                                
                                {files.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        {files.map((file, index) => (
                                            <div 
                                                key={index} 
                                                className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <FileText className="w-4 h-4 text-slate-400" />
                                                    <span className="text-sm text-slate-700">{file.name}</span>
                                                    <span className="text-xs text-slate-400">
                                                        ({(file.size / 1024).toFixed(1)} KB)
                                                    </span>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeFile(index)}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    Annulla
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-indigo-500 hover:bg-indigo-600"
                                    disabled={loading || !formData.oggetto || !formData.descrizione || !formData.categoria}
                                    data-testid="ticket-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creazione in corso...
                                        </>
                                    ) : (
                                        'Crea Ticket'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
